import { useEffect, useRef, useState } from "react";
import { hasAnalytics, getConsent, grantAnalyticsConsent, revokeAnalyticsConsent, identifyUser } from "../../analytics/posthog";
import { useAuth } from "../../context/AuthContext";
import { syncConsentToServer } from "../../lib/consentSync";

// Compact opt-in bar. While visible it reserves its own height (html.consent-open +
// --consent-h) so it never covers page content, forms or buttons.
export function CookieConsent() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(hasAnalytics && getConsent() === null);
  const ref = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.classList.remove("consent-open");
      root.style.removeProperty("--consent-h");
      return undefined;
    }
    const setH = () => root.style.setProperty("--consent-h", `${ref.current.offsetHeight}px`);
    setH();
    root.classList.add("consent-open");
    const ro = new ResizeObserver(setH);
    ro.observe(ref.current);
    return () => { ro.disconnect(); root.classList.remove("consent-open"); root.style.removeProperty("--consent-h"); };
  }, [visible]);

  if (!visible) return null;

  const decide = (granted) => {
    if (granted) {
      grantAnalyticsConsent();
      if (user && user.user_id) identifyUser(user.user_id);
    } else {
      revokeAnalyticsConsent();
    }
    if (user && user.user_id) syncConsentToServer(granted);
    setVisible(false);
  };

  return (
    <div ref={ref} data-testid="cookie-consent-banner" role="region" aria-label="Consentimiento de analítica"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-white/10 bg-obsidian/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-2.5">
        <p className="flex-1 text-xs leading-snug text-slate-300">
          Analítica propia (PostHog, UE) solo si la aceptas.{" "}
          <a href="/cookies" className="text-amber-400 hover:underline" data-testid="cookie-consent-cookies-link">Cookies</a>{" · "}
          <a href="/privacy" className="text-amber-400 hover:underline" data-testid="cookie-consent-privacy-link">Privacidad</a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button data-testid="cookie-consent-reject" onClick={() => decide(false)}
            className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/[0.07]">
            Rechazar
          </button>
          <button data-testid="cookie-consent-accept" onClick={() => decide(true)}
            className="rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black transition-colors hover:bg-amber-400">
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );
}
